/**
 * Pick expiration utilities. Whether a generated leg's game has started, which web and mobile use
 * to grey out stale picks; and whether a tracked leg's game has started (its tip, else its game
 * day), which the shared leg delete guard, DELETE /api/legs and the server's share checks use.
 */

import type { GeneratedLeg, GeneratedParlay } from './api/picksApi';

/** Returns true if the leg's game has already started (gameStartsAt <= now). */
export function isLegExpired(leg: GeneratedLeg): boolean {
  if (!leg.gameStartsAt) return false;
  return new Date(leg.gameStartsAt) <= new Date();
}

/** A tracked leg has started once its tip, else its stored game day, is at or before `now`. */
export function hasLegGameStarted(
  leg: { gameStartsAt?: Date | string | null; gameDate?: Date | string | null },
  now: Date = new Date(),
): boolean {
  const start = leg.gameStartsAt ?? leg.gameDate;
  if (!start) return false;
  return new Date(start) <= now;
}

/**
 * Returns a descriptive label for an expired leg:
 * - "Game Started — 7:30 PM" if the game started recently (within ~3 hours)
 * - "Game Ended — Mar 12, 7:30 PM" if the game likely ended (3+ hours ago)
 */
export function getExpiredLabel(leg: GeneratedLeg): string {
  if (!leg.gameStartsAt) return 'Game Started';
  const startTime = new Date(leg.gameStartsAt);
  const now = new Date();
  const hoursSinceStart = (now.getTime() - startTime.getTime()) / (1000 * 60 * 60);

  const timeStr = startTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  if (hoursSinceStart > 3) {
    const dateStr = startTime.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `Game Ended — ${dateStr}, ${timeStr}`;
  }
  return `Game Started — ${timeStr}`;
}

/** Returns true if ALL legs in a parlay have expired. */
export function isParlayFullyExpired(parlay: GeneratedParlay): boolean {
  const legs = parlay.legs ?? [];
  if (legs.length === 0) return false;
  return legs.every(isLegExpired);
}

/** Returns true if ANY leg in a parlay has expired. */
export function hasParlayExpiredLegs(parlay: GeneratedParlay): boolean {
  return (parlay.legs ?? []).some(isLegExpired);
}
